// Demo chapters for the home page. Each chapter shows a real capture from the
// app when /media/manifest.json lists one (made with
// pro/scripts/marketing-capture), and an illustration otherwise.

export const demoChapters = Object.freeze([
  Object.freeze({ id: "history", kicker: "Quick History", title: "Everything you copied, one keystroke away.", blurb: "Text, links, images and files land in History the moment you copy them. Search, pin and paste without leaving the app you're in." }),
  Object.freeze({ id: "capture", kicker: "Capture", title: "Grab the screen, a window or one exact area.", blurb: "Screenshots and screen recordings go straight into your bag, ready to paste, mark up or share." }),
  Object.freeze({ id: "markup", kicker: "Image Studio", title: "Mark it up so it reads at a glance.", blurb: "Arrows, boxes, callouts and text in seconds. Blur or pixelate anything private before it leaves your screen." }),
  Object.freeze({ id: "ocr", kicker: "Grab text", title: "Copy words out of any picture.", blurb: "Pull the text from a screenshot, a scan or a slide and paste it like you typed it." }),
  Object.freeze({ id: "video", kicker: "Video Studio", title: "Cut a clean demo, with sound.", blurb: "Trim, layer and arrange your recordings on a real timeline, then drop in sound cues from the soundboard." }),
  Object.freeze({ id: "prompts", kicker: "Text Studio", title: "The things you type every day, saved.", blurb: "Reusable snippets with blanks you fill in as you paste. Replies, prompts and templates, always in reach." }),
  Object.freeze({ id: "sync", kicker: "Pro+ sync", title: "Your bag on every computer.", blurb: "End-to-end encrypted sync across your Macs and PCs, plus Web History from any browser." }),
]);

const safeMediaPath = (value) => typeof value === "string" && /^\/media\/[a-z0-9][a-z0-9._-]{0,80}$/.test(value) ? value : "";
const safeText = (value, max = 160) => typeof value === "string" ? value.slice(0, max) : "";

// Keep only well-formed, same-site entries so a bad manifest can't inject markup
// or point the page anywhere else.
export function normalizeMediaManifest(raw) {
  const chapters = {};
  const screenshots = [];
  const ids = new Set(demoChapters.map((chapter) => chapter.id));
  const source = raw && typeof raw === "object" ? raw : {};
  for (const [id, entry] of Object.entries(source.chapters ?? {})) {
    if (!ids.has(id) || !entry || typeof entry !== "object") continue;
    const video = safeMediaPath(entry.video);
    const webm = safeMediaPath(entry.webm);
    const poster = safeMediaPath(entry.poster);
    if (video || poster) chapters[id] = { video, webm, poster };
  }
  for (const shot of Array.isArray(source.screenshots) ? source.screenshots.slice(0, 12) : []) {
    const src = safeMediaPath(shot?.src);
    if (src) screenshots.push({ src, caption: safeText(shot.caption, 80), width: Number(shot.width) || 1600, height: Number(shot.height) || 1000 });
  }
  return { chapters, screenshots };
}

export async function loadMediaManifest(fetchImpl = globalThis.fetch) {
  try {
    const response = await fetchImpl("/media/manifest.json", { headers: { accept: "application/json" } });
    if (!response.ok || !(response.headers.get("content-type") ?? "").includes("json")) return normalizeMediaManifest(null);
    return normalizeMediaManifest(await response.json());
  } catch {
    return normalizeMediaManifest(null);
  }
}
